import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { getAdminInfo } from "../../core/admin.core";
import { useUpdateCustomerEyeInfo } from "../../hooks/services/useCustomers";
import posIcons from "../../icons/pos.icons";
import { PosUserEyeInfo, PosUserEyeInfoFormData } from "../../types/user";
import EyesInfoDetails from "./EyesInfoDetails";
import EyesInfoEditor from "./EyesInfoEditor";

// interface

interface CustomerEyeInfoPanelProps {
  id: string;
  eyeInfo: PosUserEyeInfo;
  placement: "default" | "order";
}

export default function CustomerEyeInfoPanel({ eyeInfo, placement }: CustomerEyeInfoPanelProps) {
  const methods = useForm<PosUserEyeInfoFormData>();
  const queryClient = useQueryClient();
  const [isEyePanelOpen, setIsEyePanelOpen] = useState(false);

  const editCustomerEyeInfo = useUpdateCustomerEyeInfo();

  const { data: me } = useQuery({
    queryFn: () => getAdminInfo(),
    queryKey: ["admin"],
  });

  function handleToggleEyePanel() {
    setIsEyePanelOpen(!isEyePanelOpen);
  }

  useEffect(() => {
    if (eyeInfo) {
      methods.reset(eyeInfo);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eyeInfo]);

  async function onSubmit(data: PosUserEyeInfoFormData) {
    const { isSave, ...updateData } = data;

    try {
      if (placement === "default") {
        await editCustomerEyeInfo.mutateAsync({ eyeInfoId: eyeInfo.id, data: updateData });
      } else {
        if (isSave) {
          await editCustomerEyeInfo.mutateAsync({ eyeInfoId: eyeInfo.id, data: updateData });
        }
      }

      setIsEyePanelOpen(false);
      queryClient.invalidateQueries({ exact: false, queryKey: ["order"] });
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (editCustomerEyeInfo.isError) {
      toast.error("Update Failed");
      editCustomerEyeInfo.reset();
    }
    if (editCustomerEyeInfo.isSuccess) {
      toast.success("Updated successfully");
      editCustomerEyeInfo.reset();
      queryClient.invalidateQueries({ queryKey: ["customerEyeInfo"] });
      queryClient.invalidateQueries({ queryKey: ["customerInfo"] });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCustomerEyeInfo.isSuccess, editCustomerEyeInfo.error]);

  return (
    <div className="space-y-6">
      {isEyePanelOpen ? (
        <EyesInfoEditor
          handleSubmit={onSubmit} //
          methods={methods}
          placement={placement}
          handleCancel={handleToggleEyePanel}
        />
      ) : (
        <section>
          <EyesInfoDetails eyeInfo={eyeInfo} />

          <button
            id="eyeinfo-edit-button"
            type="button"
            onClick={handleToggleEyePanel}
            className="bg-her-blue text-white p-3 w-full rounded-md shadow-sm flex-center gap-2"
          >
            <span>{posIcons.editCustomerIcon}</span>
            <span>Edit Information</span>
          </button>
        </section>
      )}
    </div>
  );
}
